import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { PageHeader, Field } from "../components/UI";
import { useBrand } from "../components/AppShell";
import ImageUpload from "../components/ImageUpload";
import PasswordInput from "../components/PasswordInput";
import SidebarPreferencesSection from "../components/SidebarPreferencesSection";
import { useAuth } from "../lib/auth";
import { CreditCard, GoogleLogo, MicrosoftOutlookLogo, FloppyDisk, Trash, Hash, Buildings, UserCircle, Sliders, Briefcase, Receipt, Envelope, Handshake, MonitorPlay, Palette, Sun, Moon, Globe, Bell, FileText, ShieldCheck as Shield, Image as ImageIcon } from "@phosphor-icons/react";
// iter 70 — Notaries tab consolidated into My Organization → User drawer.
// Notaries.jsx still exists as a standalone /notaries route (kept for now).
import OrgAdminContent from "../components/OrgAdmin/OrgAdminContent";
import UnifyEngineSection from "../components/UnifyEngineSection";
import MapDesignSection from "../components/MapDesignSection";
import CollaborationPanel from "../components/CollaborationPanel";
import SessionsSection from "../components/SessionsSection";
import BrandingSection from "../components/BrandingSection";

/* ------------------------------------------------------------------------
 * iter 44 — Settings page reorganised as a LEFT-RAIL TAB layout.
 *
 *   • The settings page had grown ~800 lines tall. Splitting it into tabs
 *     keeps each surface short and lets us land Team + Notaries here too.
 *   • All section components below (NextJobNumberSection, TeamSection, etc.)
 *     are unchanged — we just gate them behind the active tab.
 * ---------------------------------------------------------------------- */
const SETTINGS_TABS = [
  { key: "general",     label: "GENERAL",       icon: UserCircle, admin: false },
  { key: "my-org",      label: "MY ORGANIZATION", icon: Buildings, admin: true },
  { key: "collaboration", label: "COLLABORATION", icon: Handshake, admin: true },
  { key: "layout",      label: "SIDEBAR LAYOUT", icon: Sliders,   admin: false },
  { key: "job-numbers", label: "JOB NUMBERING", icon: Hash,       admin: true  },
  { key: "billing",     label: "BILLING",       icon: CreditCard, admin: true  },
  { key: "stripe",      label: "STRIPE",        icon: Receipt,    admin: true  },
  { key: "email-oauth", label: "EMAIL OAUTH",   icon: Envelope,   admin: true  },
  { key: "sessions",    label: "SESSIONS",      icon: MonitorPlay, admin: false },
  { key: "branding",    label: "BRANDING",      icon: Palette,    admin: true  },
];

export default function Settings() {
  const { user } = useAuth();
  const location = useLocation();
  const [settings, setSettings] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  // iter 44 — honour `?tab=<key>` deep-links (e.g. /settings?tab=notaries).
  const initialTab = (new URLSearchParams(location.search).get("tab")) || "general";
  const [tab, setTab] = useState(initialTab);

  // Keep the URL in sync as the user clicks the sub-nav (without adding new
  // history entries).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("tab") !== tab) {
      params.set("tab", tab);
      window.history.replaceState(null, "", `${location.pathname}?${params.toString()}`);
    }
  }, [tab, location.pathname]);

  const reload = () => api.get("/company/settings").then(r => setSettings(r.data));

  useEffect(() => {
    reload();
    api.get("/uploads/status").then(r => setUploadStatus(r.data));
  }, []);

  if (!settings) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const visibleTabs = SETTINGS_TABS.filter(t => !t.admin || isAdmin);

  return (
    <div className="p-6" data-testid="settings-page">
      <PageHeader title="Settings" subtitle="WORKSPACE / PREFERENCES" />

      {uploadStatus && !uploadStatus.configured && (
        <div className="surface p-4 mb-6 border-l-4" data-testid="s3-warning" style={{ borderLeftColor: "#FACC15" }}>
          <div className="label-xs mb-1">STORAGE NOT CONFIGURED</div>
          <div className="text-sm">File uploads need Supabase Storage credentials in <code className="kbd">backend/.env</code>.</div>
        </div>
      )}

      <div className="grid gap-6" style={{ gridTemplateColumns: "220px 1fr" }}>
        {/* ---- LEFT: sub-nav rail ---- */}
        <aside data-testid="settings-subnav" style={{ position: "sticky", top: 12, alignSelf: "start" }}>
          <nav className="surface p-2 flex flex-col gap-1">
            {visibleTabs.map(t => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`flex items-center gap-2 px-3 py-2 rounded text-xs font-semibold tracking-wide text-left ${active ? "bg-primary text-primary-foreground" : "hover:bg-muted/50"}`}
                  data-testid={`settings-tab-${t.key}`}
                  aria-current={active ? "page" : undefined}
                >
                  <Icon size={14} weight={active ? "bold" : "regular"} />
                  {t.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* ---- RIGHT: active tab body ---- */}
        <div data-testid={`settings-body-${tab}`}>
          {tab === "general" && (
            <div className="space-y-6" data-testid="settings-general-body">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <CompanySection settings={settings} setSettings={setSettings} reload={reload} />
                <ProfileSection user={user} />
              </div>
              {/* iter 70.5 — migrated from the retired Your Profile drawer. Stacked single-column. */}
              <AppearanceSection />
              <PreferencesSection />
              <NotificationsSection />
              <AffidavitSignatureSection />
              <SecuritySection />
            </div>
          )}
          {tab === "layout" && <SidebarPreferencesSection />}
          {tab === "my-org" && isAdmin && (
            <div className="surface p-5 space-y-6" data-testid="settings-my-org-body">
              <div className="mb-3">
                <div className="label-xs mb-1">MY ORGANIZATION</div>
                <div className="text-sm text-muted-foreground">
                  Manage your org&apos;s users, office locations, and departments. Changes apply only to <strong>{settings?.name || user?.org_name || "your org"}</strong>.
                </div>
              </div>
              <OrgAdminContent
                orgId={user?.org_id}
                orgName={settings?.name || user?.org_name}
                embedded
                active={tab === "my-org"}
              />
              {/* iter 73.15 Phase H — Map Design (per user spec: Settings >
                  My Organization > Map Design). Super_admin/admin gated by
                  the outer `isAdmin` check. */}
              <MapDesignSection />
              {/* iter 72 — Org Unification Engine (super_admin-only; soft-hidden for others). */}
              <UnifyEngineSection />
            </div>
          )}
          {tab === "job-numbers" && isAdmin && <NextJobNumberSection settings={settings} reload={reload} />}
          {tab === "billing" && isAdmin && <BillingSection />}
          {tab === "stripe" && isAdmin && <StripeSection settings={settings} reload={reload} />}
          {tab === "email-oauth" && isAdmin && <EmailOAuthSection settings={settings} reload={reload} />}
          {tab === "collaboration" && isAdmin && <CollaborationPanel />}
          {tab === "sessions" && <SessionsSection canViewOrg={isAdmin || user?.role === "super_admin"} />}
          {tab === "branding" && isAdmin && <BrandingSection />}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------ *
 * iter 35 — Admin override for the next job_number generated by /jobs POST.
 *   • If set, the next job created consumes this value and clears the field.
 *   • If empty, jobs fall back to JOB-NNNNN auto-incrementing sequence.
 * ------------------------------------------------------------------------ */
function NextJobNumberSection({ settings, reload }) {
  const [val, setVal] = useState(settings.next_job_number || "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const save = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const r = await api.put("/company/next-job-number", { next_job_number: val });
      setMsg(r.data.next_job_number
        ? `Saved. The next job will be numbered "${r.data.next_job_number}".`
        : "Cleared. The next job will use the JOB-NNNNN sequence.");
      reload();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <section className="surface p-6 mb-6" data-testid="next-job-number-section">
      <div className="flex items-center gap-3 mb-3">
        <Hash size={20} weight="bold" />
        <div>
          <div className="label-xs">JOB NUMBERING</div>
          <h2 className="heading text-xl">Next job number</h2>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Optional one-time override. When set, the next job created (via the New Job drawer or intake approval) uses this exact value, then this field is automatically cleared.
        Leave blank to continue the sequential count — each new job gets the next integer after your org&apos;s highest numeric job number (e.g. <code className="kbd">5</code> → <code className="kbd">6</code>).
      </p>
      <div className="flex items-end gap-2 max-w-md">
        <Field label="NEXT JOB NUMBER">
          <input
            className="input mono"
            placeholder="e.g. 100 or SMITH-001"
            value={val}
            onChange={e => setVal(e.target.value)}
            data-testid="next-job-number-input"
          />
        </Field>
        <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="save-next-job-number-btn">
          <FloppyDisk size={14} /> SAVE
        </button>
      </div>
      {err && <div className="text-xs text-destructive mt-2" data-testid="next-job-number-error">{err}</div>}
      {msg && <div className="text-xs text-green-700 mt-2" data-testid="next-job-number-msg">{msg}</div>}
    </section>
  );
}

function CompanySection({ settings, setSettings, reload }) {
  return (
    <section className="surface p-6">
      <div className="label-xs mb-2">ORGANIZATION</div>
      <h2 className="heading text-xl mb-4">{settings.name}</h2>
      <div className="label-xs mb-3">LOGO (appears on invoice PDFs)</div>
      <ImageUpload
        endpoint="/uploads/company/logo"
        currentUrl={settings.logo_url}
        onUploaded={(url) => { setSettings({ ...settings, logo_url: url }); reload(); }}
        size={100} round={false} label="Upload logo" testid="company-logo-upload"
      />
      <div className="mt-4 pt-4 border-t border-border">
        <div className="label-xs">PLAN</div>
        <div className="mono">{(settings.plan || "—").toUpperCase()}</div>
      </div>
    </section>
  );
}

function ProfileSection({ user }) {
  return (
    <section className="surface p-6">
      <div className="label-xs mb-2">PROFILE</div>
      <h2 className="heading text-xl mb-4">{user?.name}</h2>
      <div className="label-xs mb-3">AVATAR</div>
      <ImageUpload
        endpoint="/uploads/user/avatar"
        currentUrl={user?.avatar_url}
        onUploaded={() => window.location.reload()}
        testid="user-avatar-upload"
      />
      <div className="mt-4 pt-4 border-t border-border space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span className="mono">{user?.email}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Role</span><span className="badge badge-default">{user?.role}</span></div>
      </div>
    </section>
  );
}

function BillingSection() {
  const { platformName } = useBrand();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    try { const r = await api.get("/billing/me"); setInfo(r.data); }
    catch (e) { setErr(formatErr(e)); }
  };

  useEffect(() => {
    load();
    // Handle Stripe Checkout return
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("billing_session");
    const cancelled = params.get("billing_cancelled");
    if (sid) {
      (async () => {
        try { const r = await api.post(`/billing/sync/${sid}`, {}); setInfo(r.data); setMsg("Payment method added — subscription active."); }
        catch (e) { setErr(formatErr(e)); }
        finally { window.history.replaceState({}, "", window.location.pathname); }
      })();
    } else if (cancelled) {
      setMsg("Checkout cancelled.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const startCheckout = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/billing/checkout", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { setErr(formatErr(e)); setBusy(false); }
  };

  const openPortal = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { setErr(formatErr(e)); setBusy(false); }
  };

  if (!info) return (
    <section className="surface p-6 mb-6" data-testid="billing-section">
      <div className="label-xs mb-2">PLATFORM BILLING</div>
      <div className="text-sm text-muted-foreground">Loading billing…</div>
      {err && <div className="text-xs text-destructive mt-2" data-testid="billing-error">{err}</div>}
    </section>
  );

  const pricing = info.pricing || {};
  const trialPct = Math.min(100, Math.round((info.trial_jobs_used / Math.max(1, info.trial_jobs_limit)) * 100));
  const showAddCard = !info.payment_method;
  const hasSub = !!info.subscription_id;

  return (
    <section className="surface p-6 mb-6" data-testid="billing-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <CreditCard size={20} weight="bold" />
          <div>
            <div className="label-xs">PLATFORM BILLING & USAGE</div>
            <h2 className="heading text-xl">{platformName} subscription</h2>
          </div>
        </div>
        <div>
          {info.billing_active && <span className="badge badge-green" data-testid="billing-status">ACTIVE</span>}
          {!info.billing_active && !info.trial_over && <span className="badge badge-yellow" data-testid="billing-status">TRIAL</span>}
          {!info.billing_active && info.trial_over && <span className="badge badge-red" data-testid="billing-status">TRIAL ENDED</span>}
        </div>
      </div>

      <div className="text-sm mb-4">
        Pricing: <span className="mono font-semibold">${(pricing.base_per_month_usd ?? 10).toFixed(2)}/mo</span> base + <span className="mono font-semibold">${(pricing.per_job_usd ?? 0.25).toFixed(2)} per job</span>.
        First month or first {info.trial_jobs_limit} jobs are <strong>free</strong>.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="surface p-4">
          <div className="label-xs mb-1">TRIAL JOBS</div>
          <div className="mono text-2xl" data-testid="trial-jobs-used">{info.trial_jobs_used} / {info.trial_jobs_limit}</div>
          <div className="h-1.5 bg-muted rounded mt-2 overflow-hidden">
            <div className="h-full bg-primary" style={{ width: `${trialPct}%` }} />
          </div>
          <div className="text-xs text-muted-foreground mt-1">{info.trial_jobs_remaining} remaining</div>
        </div>
        <div className="surface p-4">
          <div className="label-xs mb-1">TRIAL DAYS LEFT</div>
          <div className="mono text-2xl" data-testid="trial-days-remaining">{info.trial_days_remaining}</div>
          <div className="text-xs text-muted-foreground mt-1">
            Ends {new Date(info.trial_expires_at).toLocaleDateString()}
          </div>
        </div>
        <div className="surface p-4">
          <div className="label-xs mb-1">NEXT BILL</div>
          <div className="mono text-base" data-testid="next-billing-date">
            {info.next_billing_date ? new Date(info.next_billing_date).toLocaleDateString() : "—"}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {info.payment_method ? `${info.payment_method.brand?.toUpperCase()} •••• ${info.payment_method.last4}` : "No card on file"}
          </div>
        </div>
      </div>

      {info.trial_over && !info.billing_active && (
        <div className="surface p-3 mb-4 border-l-4" style={{ borderLeftColor: "#EF4444" }} data-testid="trial-ended-banner">
          <div className="label-xs mb-1" style={{ color: "#EF4444" }}>TRIAL ENDED — ADD A CARD TO CONTINUE</div>
          <div className="text-sm">
            {info.trial_limit_hit
              ? `You've reached the ${info.trial_jobs_limit}-job free limit.`
              : `Your ${pricing.base_per_month_usd ? "" : ""}free month is over.`}
            New orders are blocked until a payment method is added.
          </div>
        </div>
      )}

      {err && <div className="text-xs text-destructive mb-2" data-testid="billing-error">{err}</div>}
      {msg && <div className="text-xs text-green-700 mb-2" data-testid="billing-msg">{msg}</div>}

      <div className="flex gap-2 flex-wrap">
        {showAddCard && (
          <button className="btn btn-primary" onClick={startCheckout} disabled={busy} data-testid="add-card-btn">
            <CreditCard size={14} /> {hasSub ? "RESUME SUBSCRIPTION" : "ADD CARD & SUBSCRIBE"}
          </button>
        )}
        {info.stripe_customer_id && (
          <button className="btn btn-secondary" onClick={openPortal} disabled={busy} data-testid="manage-billing-btn">
            MANAGE BILLING
          </button>
        )}
      </div>
    </section>
  );
}

function StripeSection({ settings, reload }) {
  const { platformName, platformPossessive } = useBrand();
  const s = settings.stripe;
  const connect = s.connect || {};
  const [form, setForm] = useState({ stripe_secret_key: "", stripe_publishable_key: settings.stripe?.stripe_publishable_key || "", stripe_webhook_secret: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  // iter 47 — manual sk_… form is collapsed by default once Connect is the
  // recommended path; it stays open if the org already pasted a key.
  const [advancedOpen, setAdvancedOpen] = useState(!connect.connected && !!s.stripe_publishable_key);

  // Surface ?connect_success / ?connect_error from the OAuth callback.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("connect_success")) {
      setMsg("Stripe account connected ✅");
      reload();
      p.delete("connect_success");
      window.history.replaceState(null, "", `${window.location.pathname}?${p.toString()}`);
    } else if (p.get("connect_error")) {
      setErr(`Stripe Connect error: ${p.get("connect_error")} ${p.get("desc") || ""}`);
      p.delete("connect_error"); p.delete("desc");
      window.history.replaceState(null, "", `${window.location.pathname}?${p.toString()}`);
    }
  }, []); // eslint-disable-line

  const beginConnect = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.get("/stripe/connect/start");
      window.location.href = r.data.authorize_url;
    } catch (e) { setErr(formatErr(e)); setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm(`Disconnect your Stripe account from ${platformName}? Your customers will no longer be able to pay invoices online until you reconnect.`)) return;
    setBusy(true); setErr("");
    try { await api.post("/stripe/connect/disconnect"); setMsg("Disconnected"); reload(); }
    catch (e) { setErr(formatErr(e)); }
    setBusy(false);
  };

  const save = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const payload = {};
      if (form.stripe_secret_key) payload.stripe_secret_key = form.stripe_secret_key;
      payload.stripe_publishable_key = form.stripe_publishable_key;
      if (form.stripe_webhook_secret) payload.stripe_webhook_secret = form.stripe_webhook_secret;
      await api.put("/company/stripe", payload);
      setForm({ ...form, stripe_secret_key: "", stripe_webhook_secret: "" });
      setMsg("Saved");
      reload();
    } catch (e) { setErr(formatErr(e)); }
    setBusy(false);
  };

  const clear = async () => {
    if (!window.confirm(`Remove your Stripe credentials? Your customer invoices will no longer be payable online until you add a new key. (${platformPossessive.charAt(0).toUpperCase() + platformPossessive.slice(1)} platform Stripe is not a substitute — it only handles your SaaS subscription.)`)) return;
    setBusy(true);
    try { await api.delete("/company/stripe"); setMsg("Cleared"); reload(); }
    catch (e) { setErr(formatErr(e)); }
    setBusy(false);
  };

  return (
    <section className="surface p-6 mb-6" data-testid="stripe-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <CreditCard size={20} weight="bold" />
          <div>
            <div className="label-xs">CUSTOMER INVOICE PAYMENTS</div>
            <h2 className="heading text-xl">Your Stripe account</h2>
          </div>
        </div>
        <div>
          {(connect.connected || s.configured) && <span className="badge badge-green" data-testid="stripe-status">CONNECTED</span>}
          {!(connect.connected || s.configured) && <span className="badge badge-red" data-testid="stripe-status">NOT CONNECTED</span>}
        </div>
      </div>

      <div
        className="surface p-3 mb-4 border-l-4 text-xs"
        style={{ borderLeftColor: (connect.connected || s.configured) ? "#16a34a" : "#dc2626" }}
        data-testid="stripe-policy-banner"
      >
        <div className="font-semibold mb-1">
          {(connect.connected || s.configured) ? "Customer payments route to your Stripe account." : "Your customers cannot pay invoices online until you connect Stripe."}
        </div>
        {platformPossessive.charAt(0).toUpperCase() + platformPossessive.slice(1)} platform Stripe key is reserved exclusively for your{" "}
        <b>{platformName} subscription</b> billing. Customer invoices settle directly
        into your own Stripe account so chargebacks, refunds, and 1099-K
        reporting stay with you.
      </div>

      {/* ---- Stripe Connect (preferred) ---- */}
      {connect.connected ? (
        <div className="surface p-4 mb-4 flex items-center justify-between" data-testid="stripe-connected-block">
          <div>
            <div className="font-semibold">Connected to Stripe</div>
            <div className="text-xs text-muted-foreground mono mt-1">
              {connect.stripe_account_id}{" "}
              {connect.livemode === false && <span className="badge badge-yellow ml-1">TEST MODE</span>}
              {connect.livemode === true && <span className="badge badge-green ml-1">LIVE</span>}
            </div>
            {connect.connected_at && (
              <div className="text-xs text-muted-foreground mt-1">
                Connected {new Date(connect.connected_at).toLocaleString()}
              </div>
            )}
          </div>
          <button className="btn btn-ghost" onClick={disconnect} disabled={busy} data-testid="stripe-disconnect-btn">DISCONNECT</button>
        </div>
      ) : connect.platform_ready ? (
        <div className="surface p-4 mb-4 text-center" data-testid="stripe-connect-cta">
          <button
            className="btn btn-primary"
            onClick={beginConnect}
            disabled={busy}
            data-testid="stripe-connect-btn"
            style={{ background: "#635BFF", borderColor: "#635BFF", color: "#fff", padding: "10px 24px", fontWeight: 700 }}
          >
            CONNECT WITH STRIPE →
          </button>
          <div className="text-xs text-muted-foreground mt-2">
            You&apos;ll be redirected to Stripe to sign in (or create a new account)
            and authorize {platformName}. We only store your Stripe account ID
            and a refresh token — never your secret key.
          </div>
        </div>
      ) : (
        <div className="p-3 mb-4 text-xs" data-testid="stripe-connect-platform-missing" style={{ background: "#fef3c7", borderRadius: 6 }}>
          <b>Stripe Connect is not enabled on the {platformName} platform yet.</b>{" "}
          Platform admin: enable Connect at{" "}
          <a className="link" href="https://dashboard.stripe.com/settings/connect" target="_blank" rel="noreferrer">dashboard.stripe.com/settings/connect</a>,
          then add <code className="kbd">STRIPE_CONNECT_CLIENT_ID=ca_…</code> to <code className="kbd">backend/.env</code>. The manual API key form below remains usable in the meantime.
        </div>
      )}

      {/* ---- Advanced: manual sk_… paste (legacy / fallback) ---- */}
      <div className="border border-border rounded">
        <button
          type="button"
          className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wide hover:bg-muted/40"
          onClick={() => setAdvancedOpen(o => !o)}
          data-testid="stripe-advanced-toggle"
        >
          <span>⚙️ Advanced: paste API keys manually ({advancedOpen ? "hide" : "show"})</span>
          <span className="text-muted-foreground">{advancedOpen ? "▼" : "▶"}</span>
        </button>
        {!advancedOpen && null}
        {advancedOpen && (<>
      <div className="text-xs text-muted-foreground mb-4 mt-3 px-3">
        Get keys from <span className="kbd">dashboard.stripe.com → Developers → API keys</span>.
        Secret key starts with <code className="kbd">sk_live_</code> or <code className="kbd">sk_test_</code>.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="SECRET KEY (sk_…)">
          <PasswordInput
            className="input mono"
            placeholder={s.stripe_secret_key_masked || "sk_test_..."}
            value={form.stripe_secret_key}
            onChange={e => setForm({ ...form, stripe_secret_key: e.target.value })}
            data-testid="stripe-secret-input"
          />
          {s.stripe_secret_key_masked && <div className="text-xs text-muted-foreground mt-1">Current: <span className="mono">{s.stripe_secret_key_masked}</span></div>}
        </Field>
        <Field label="PUBLISHABLE KEY (pk_…)">
          <input
            className="input mono"
            placeholder="pk_test_..."
            value={form.stripe_publishable_key}
            onChange={e => setForm({ ...form, stripe_publishable_key: e.target.value })}
            data-testid="stripe-publishable-input"
          />
        </Field>
        <Field label="WEBHOOK SECRET (whsec_…)">
          <PasswordInput
            className="input mono"
            placeholder={s.stripe_webhook_secret_masked || "whsec_..."}
            value={form.stripe_webhook_secret}
            onChange={e => setForm({ ...form, stripe_webhook_secret: e.target.value })}
            data-testid="stripe-webhook-input"
          />
          {s.stripe_webhook_secret_masked && <div className="text-xs text-muted-foreground mt-1">Current: <span className="mono">{s.stripe_webhook_secret_masked}</span></div>}
        </Field>
      </div>
      {err && <div className="text-xs text-destructive mt-3">{err}</div>}
      {msg && <div className="text-xs text-green-700 mt-3">{msg}</div>}
      <div className="flex gap-2 mt-4">
        <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="save-stripe-btn">
          <FloppyDisk size={14} /> SAVE
        </button>
        {(s.stripe_secret_key_masked || s.stripe_webhook_secret_masked) && (
          <button className="btn btn-secondary" onClick={clear} disabled={busy} data-testid="clear-stripe-btn">
            <Trash size={14} /> REMOVE
          </button>
        )}
      </div>
      </>)}{/* /advanced manual paste */}
      </div>{/* /advanced toggle wrapper */}
      {msg && <div className="text-green-600 text-xs mt-2">{msg}</div>}
      {err && <div className="text-destructive text-xs mt-2">{err}</div>}
    </section>
  );
}

function EmailOAuthSection({ settings, reload }) {
  const { platformName } = useBrand();
  const g = settings.gmail; const o = settings.outlook;
  const [form, setForm] = useState({
    gmail_client_id: g.client_id || "", gmail_client_secret: "",
    outlook_client_id: o.client_id || "", outlook_client_secret: "",
    outlook_tenant_id: o.tenant_id || "common",
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  // iter 45 — pull live redirect URI from backend so the guide always shows
  // the URI Google Cloud needs to be configured with (matches `_redirect_uri()`
  // in routers/email_router.py).
  const [oauthStatus, setOauthStatus] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    api.get("/email/oauth/status").then(r => setOauthStatus(r.data)).catch(() => {});
  }, []);
  const copyRedirect = async () => {
    if (!oauthStatus?.gmail_redirect_uri) return;
    try {
      await navigator.clipboard.writeText(oauthStatus.gmail_redirect_uri);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard may be blocked */ }
  };

  const save = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const payload = {
        gmail_client_id: form.gmail_client_id,
        outlook_client_id: form.outlook_client_id,
        outlook_tenant_id: form.outlook_tenant_id,
      };
      if (form.gmail_client_secret) payload.gmail_client_secret = form.gmail_client_secret;
      if (form.outlook_client_secret) payload.outlook_client_secret = form.outlook_client_secret;
      await api.put("/company/email-oauth", payload);
      setForm({ ...form, gmail_client_secret: "", outlook_client_secret: "" });
      setMsg("Saved");
      reload();
    } catch (e) { setErr(formatErr(e)); }
    setBusy(false);
  };

  return (
    <section className="surface p-6 mb-6" data-testid="email-oauth-section">
      <h2 className="heading text-xl mb-1">Email OAuth credentials</h2>
      <div className="label-xs mb-4">CONNECT YOUR GMAIL & OUTLOOK ACCOUNTS</div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-3" data-testid="gmail-block">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><GoogleLogo size={18} weight="bold" /> <span className="font-semibold">Gmail / G Suite</span></div>
            {g.configured && !g.using_env_fallback && <span className="badge badge-green">CONFIGURED</span>}
            {g.using_env_fallback && <span className="badge badge-yellow">PLATFORM DEFAULT</span>}
            {!g.configured && <span className="badge badge-gray">NOT SET</span>}
          </div>

          {/* iter 45 — collapsible step-by-step guide so any org admin can
              create their own Google Cloud OAuth Client without leaving the
              app. The redirect URI shown here is read live from the backend
              (must match exactly what Google sends back to /api/email/callback/gmail). */}
          <div className="border border-border rounded" data-testid="gmail-setup-guide">
            <button
              type="button"
              className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wide hover:bg-muted/40"
              onClick={() => setGuideOpen(o => !o)}
              data-testid="gmail-setup-guide-toggle"
            >
              <span>📘 How to create your Google Cloud OAuth client ({guideOpen ? "hide" : "show"})</span>
              <span className="text-muted-foreground">{guideOpen ? "▼" : "▶"}</span>
            </button>
            {guideOpen && (
              <div className="px-3 pb-3 pt-1 text-xs space-y-2" data-testid="gmail-setup-guide-body">
                <div className="surface p-2 flex items-center gap-2" data-testid="gmail-redirect-block">
                  <span className="font-semibold uppercase">Your redirect URI:</span>
                  <code className="mono flex-1 break-all">
                    {oauthStatus?.gmail_redirect_uri || "loading…"}
                  </code>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={copyRedirect}
                    disabled={!oauthStatus?.gmail_redirect_uri}
                    data-testid="gmail-redirect-copy"
                  >{copied ? "✓ COPIED" : "COPY"}</button>
                </div>

                <ol className="list-decimal ml-5 space-y-2">
                  <li>
                    <b>Open the Google Cloud Console:</b>{" "}
                    <a className="link" href="https://console.cloud.google.com/" target="_blank" rel="noreferrer">
                      console.cloud.google.com
                    </a>. Create a new project (top-left project picker → <b>New Project</b>) — name it
                    e.g. <code className="kbd">{platformName} — Gmail Connect</code>. Wait ~10s for it to provision, then switch to it.
                  </li>
                  <li>
                    <b>Enable the Gmail API.</b> Navigate to <code className="kbd">APIs &amp; Services → Library</code>,
                    search <b>Gmail API</b>, click it, then <b>Enable</b>. (You don&apos;t need to enable the People API.)
                  </li>
                  <li>
                    <b>Configure the OAuth consent screen.</b> Go to{" "}
                    <code className="kbd">APIs &amp; Services → OAuth consent screen</code>. Pick <b>External</b> (or
                    Internal if your domain is Google Workspace-managed), then fill in:
                    <ul className="list-disc ml-5 mt-1 space-y-0.5">
                      <li><b>App name:</b> the name your users will see at the Google permission prompt (e.g. <i>{platformName}</i>)</li>
                      <li><b>User support email:</b> any address on your domain</li>
                      <li><b>App logo:</b> optional but recommended (shows on the consent screen)</li>
                      <li><b>Authorized domains:</b> the host part of your app URL — copy from the redirect URI box above (everything between <code className="kbd">https://</code> and the next <code className="kbd">/</code>)</li>
                      <li><b>Developer contact email:</b> required</li>
                    </ul>
                    Save and continue.
                  </li>
                  <li>
                    <b>Add the OAuth scopes</b> LogBook Legal needs. Click <b>Add or Remove Scopes</b> and paste-search each of these:
                    <ul className="list-disc ml-5 mt-1 space-y-0.5 mono">
                      <li>.../auth/gmail.readonly &nbsp;<span className="text-muted-foreground font-sans">(read incoming mail for Unified Inbox)</span></li>
                      <li>.../auth/gmail.send &nbsp;<span className="text-muted-foreground font-sans">(send Proof-of-Service emails on your behalf)</span></li>
                      <li>.../auth/userinfo.email &nbsp;<span className="text-muted-foreground font-sans">(identify the connected mailbox)</span></li>
                    </ul>
                    Save and continue. Two of these are flagged &quot;Sensitive&quot; / &quot;Restricted&quot; — that&apos;s expected. While the app is in <b>Testing</b> mode they work
                    fine for up to 100 added test users. When you&apos;re ready for the whole org, click <b>Publish App</b> (Google may require a brand-verification review for the restricted gmail.readonly scope — typically ~7 days).
                  </li>
                  <li>
                    <b>Add yourself (and any teammate) as a Test user</b> on the OAuth consent screen → <b>Test users</b> → <b>+ Add Users</b>. Until the app is published, only these emails can connect.
                  </li>
                  <li>
                    <b>Create the OAuth 2.0 Client ID.</b> Go to{" "}
                    <code className="kbd">APIs &amp; Services → Credentials</code> → <b>Create Credentials</b> → <b>OAuth Client ID</b>.
                    <ul className="list-disc ml-5 mt-1 space-y-0.5">
                      <li><b>Application type:</b> <i>Web application</i></li>
                      <li><b>Name:</b> e.g. <i>LogBook Legal Web Client</i></li>
                      <li>
                        <b>Authorized redirect URIs:</b> click <b>+ Add URI</b> and paste the redirect URI from the box above (exactly — no trailing slash, scheme + host + <code className="kbd">/api/email/callback/gmail</code>).
                      </li>
                    </ul>
                    Click <b>Create</b>. A dialog will show you the new <b>Client ID</b> and <b>Client Secret</b>.
                  </li>
                  <li>
                    <b>Paste those two values</b> into the Client ID and Client Secret fields below this guide, then click <b>SAVE EMAIL OAUTH</b>. You can then close the guide and connect any team member&apos;s mailbox via <code className="kbd">Email Inbox → Connect Gmail</code>.
                  </li>
                </ol>

                <div className="mt-2 p-2 surface text-xs">
                  <b>Common gotchas:</b>
                  <ul className="list-disc ml-5 mt-1 space-y-0.5">
                    <li><b>redirect_uri_mismatch</b> — the URI in step 6 must be byte-identical to the one shown above. Watch for trailing slashes and <code className="kbd">http://</code> vs <code className="kbd">https://</code>.</li>
                    <li><b>access_denied</b> while the app is in <i>Testing</i> mode — the connecting email must be added as a Test user (step 5).</li>
                    <li><b>invalid_scope</b> — re-check the scope list in step 4 has all three exactly as shown.</li>
                    <li><b>Refresh-token missing</b> after first connect — the LogBook backend already requests <code className="kbd">access_type=offline&amp;prompt=consent</code> so this should always work. If you ever see it, disconnect the account and reconnect once.</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          <div className="text-xs text-muted-foreground">
            From <span className="kbd">console.cloud.google.com → APIs &amp; Services → Credentials</span> (OAuth 2.0 Client IDs).
          </div>
          <Field label="CLIENT ID">
            <input className="input mono" value={form.gmail_client_id} onChange={e => setForm({ ...form, gmail_client_id: e.target.value })} data-testid="gmail-client-id-input" />
          </Field>
          <Field label="CLIENT SECRET">
            <PasswordInput className="input mono" placeholder={g.client_secret_masked || ""} value={form.gmail_client_secret} onChange={e => setForm({ ...form, gmail_client_secret: e.target.value })} data-testid="gmail-client-secret-input" />
            {g.client_secret_masked && <div className="text-xs text-muted-foreground mt-1">Current: <span className="mono">{g.client_secret_masked}</span></div>}
          </Field>
        </div>

        <div className="space-y-3" data-testid="outlook-block">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><MicrosoftOutlookLogo size={18} weight="bold" /> <span className="font-semibold">Outlook / Microsoft 365</span></div>
            {o.configured && !o.using_env_fallback && <span className="badge badge-green">CONFIGURED</span>}
            {o.using_env_fallback && <span className="badge badge-yellow">PLATFORM DEFAULT</span>}
            {!o.configured && <span className="badge badge-gray">NOT SET</span>}
          </div>
          <div className="text-xs text-muted-foreground">
            From <span className="kbd">portal.azure.com → App registrations</span> (your application).
          </div>
          <Field label="CLIENT ID">
            <input className="input mono" value={form.outlook_client_id} onChange={e => setForm({ ...form, outlook_client_id: e.target.value })} data-testid="outlook-client-id-input" />
          </Field>
          <Field label="CLIENT SECRET">
            <PasswordInput className="input mono" placeholder={o.client_secret_masked || ""} value={form.outlook_client_secret} onChange={e => setForm({ ...form, outlook_client_secret: e.target.value })} data-testid="outlook-client-secret-input" />
            {o.client_secret_masked && <div className="text-xs text-muted-foreground mt-1">Current: <span className="mono">{o.client_secret_masked}</span></div>}
          </Field>
          <Field label="TENANT ID">
            <input className="input mono" value={form.outlook_tenant_id} onChange={e => setForm({ ...form, outlook_tenant_id: e.target.value })} data-testid="outlook-tenant-input" />
          </Field>
        </div>
      </div>

      {err && <div className="text-xs text-destructive mt-3">{err}</div>}
      {msg && <div className="text-xs text-green-700 mt-3">{msg}</div>}
      <div className="mt-4">
        <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="save-email-oauth-btn">
          <FloppyDisk size={14} /> SAVE EMAIL OAUTH
        </button>
      </div>
    </section>
  );
}

/* ========================================================================== *
 * iter 70.5 — User-scoped preference sections (migrated from the retired
 * UserProfileDrawer). Each section patches /api/users/me with the relevant
 * subset of fields. They live in Settings → GENERAL, stacked single-column.
 * ========================================================================== */

const NOTIF_EVENTS = [
  ["job_assigned",     "Job assigned to me"],
  ["task_assigned",    "Task assigned to me"],
  ["intake_assigned",  "Intake email assigned to me"],
  ["job_acknowledged", "Job acknowledged by host"],
  ["invoice_paid",     "Invoice paid"],
  ["service_attempt",  "Service attempt logged"],
  ["mention",          "@-mentions in messages"],
];

/** Save the supplied patch object via PATCH /api/users/me and refresh /auth/me. */
function useMePatch() {
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async (payload) => {
    setBusy(true); setErr("");
    try {
      await api.patch("/users/me", payload);
      await refresh?.();
      toast.success("Saved.");
    } catch (e) {
      const msg = formatErr(e);
      setErr(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };
  return { save, busy, err };
}

function AppearanceSection() {
  const { user } = useAuth();
  const { save, busy, err } = useMePatch();
  const theme = user?.theme || "light";
  return (
    <section className="surface p-6" data-testid="appearance-section">
      <div className="flex items-center gap-3 mb-3">
        <Sun size={20} weight="bold" />
        <div>
          <div className="label-xs">APPEARANCE</div>
          <h2 className="heading text-xl">Theme</h2>
        </div>
      </div>
      <div className="flex gap-1" data-testid="appearance-theme">
        {[
          { v: "light", label: "Light", icon: Sun },
          { v: "dark",  label: "Dark",  icon: Moon },
        ].map(o => (
          <button
            key={o.v}
            type="button"
            className={`btn btn-sm ${theme === o.v ? "btn-primary" : "btn-ghost"}`}
            disabled={busy || theme === o.v}
            onClick={() => save({ theme: o.v })}
            data-testid={`appearance-theme-${o.v}`}
          >
            <o.icon size={12} /> {o.label}
          </button>
        ))}
      </div>
      {err && <div className="text-xs text-destructive mt-2" data-testid="appearance-error">{err}</div>}
    </section>
  );
}

function PreferencesSection() {
  const { user } = useAuth();
  const { save, busy, err } = useMePatch();
  const [form, setForm] = useState({
    maps_preference: user?.maps_preference || "google",
    default_landing_page: user?.default_landing_page || "",
    calendar_default_view: user?.calendar_default_view || "week",
    locale: user?.locale || "",
    timezone: user?.timezone || "",
  });
  useEffect(() => {
    setForm({
      maps_preference: user?.maps_preference || "google",
      default_landing_page: user?.default_landing_page || "",
      calendar_default_view: user?.calendar_default_view || "week",
      locale: user?.locale || "",
      timezone: user?.timezone || "",
    });
  }, [user?.id, user?.maps_preference, user?.default_landing_page, user?.calendar_default_view, user?.locale, user?.timezone]);

  return (
    <section className="surface p-6" data-testid="preferences-section">
      <div className="flex items-center gap-3 mb-3">
        <Globe size={20} weight="bold" />
        <div>
          <div className="label-xs">PREFERENCES</div>
          <h2 className="heading text-xl">Maps, landing page, calendar, locale, timezone</h2>
        </div>
      </div>
      <div className="space-y-3">
        <Field label="MAPS">
          <div className="flex gap-1" data-testid="preferences-maps">
            {[
              { v: "google", label: "Google Maps" },
              { v: "apple",  label: "Apple Maps" },
            ].map(o => (
              <button
                key={o.v}
                type="button"
                className={`btn btn-sm ${form.maps_preference === o.v ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setForm({ ...form, maps_preference: o.v })}
                data-testid={`preferences-maps-${o.v}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </Field>
        <Field label="DEFAULT LANDING PAGE">
          <select
            className="input w-full"
            value={form.default_landing_page}
            onChange={(e) => setForm({ ...form, default_landing_page: e.target.value })}
            data-testid="preferences-landing"
          >
            <option value="">/  (Dashboard — default)</option>
            <option value="/jobs">/jobs</option>
            <option value="/calendar">/calendar</option>
            <option value="/intake">/intake (Intake review)</option>
            <option value="/routes">/routes</option>
            <option value="/tasks">/tasks</option>
          </select>
        </Field>
        <Field label="CALENDAR DEFAULT VIEW">
          <div className="flex gap-1" data-testid="preferences-cal-view">
            {[
              { v: "day",   label: "Day" },
              { v: "week",  label: "Week" },
              { v: "month", label: "Month" },
            ].map(o => (
              <button
                key={o.v}
                type="button"
                className={`btn btn-sm ${form.calendar_default_view === o.v ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setForm({ ...form, calendar_default_view: o.v })}
                data-testid={`preferences-cal-view-${o.v}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="LOCALE">
            <input
              className="input w-full"
              value={form.locale}
              onChange={(e) => setForm({ ...form, locale: e.target.value })}
              placeholder="en-US"
              data-testid="preferences-locale"
            />
          </Field>
          <Field label="TIMEZONE">
            <input
              className="input w-full"
              value={form.timezone}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
              placeholder="America/Chicago"
              data-testid="preferences-tz"
            />
          </Field>
        </div>
      </div>
      <div className="mt-4">
        <button
          className="btn btn-primary"
          disabled={busy}
          onClick={() => save({
            maps_preference: form.maps_preference,
            default_landing_page: form.default_landing_page || null,
            calendar_default_view: form.calendar_default_view,
            locale: form.locale || null,
            timezone: form.timezone || null,
          })}
          data-testid="preferences-save-btn"
        >
          <FloppyDisk size={14} /> SAVE PREFERENCES
        </button>
      </div>
      {err && <div className="text-xs text-destructive mt-2" data-testid="preferences-error">{err}</div>}
    </section>
  );
}

function NotificationsSection() {
  const { user } = useAuth();
  const { save, busy, err } = useMePatch();
  const [prefs, setPrefs] = useState(user?.notification_prefs || {});
  useEffect(() => { setPrefs(user?.notification_prefs || {}); }, [user?.id, user?.notification_prefs]);

  const toggle = (k, checked) => {
    const next = { ...prefs, [k]: checked };
    setPrefs(next);
    save({ notification_prefs: next });
  };

  return (
    <section className="surface p-6" data-testid="notifications-section">
      <div className="flex items-center gap-3 mb-3">
        <Bell size={20} weight="bold" />
        <div>
          <div className="label-xs">NOTIFICATIONS</div>
          <h2 className="heading text-xl">Which events ping your bell?</h2>
        </div>
      </div>
      <div className="space-y-1">
        {NOTIF_EVENTS.map(([k, label]) => (
          <label
            key={k}
            className="flex items-center gap-2 py-1 cursor-pointer"
            data-testid={`notifications-${k}-row`}
          >
            <input
              type="checkbox"
              disabled={busy}
              checked={prefs?.[k] ?? true}
              onChange={(e) => toggle(k, e.target.checked)}
              data-testid={`notifications-${k}-checkbox`}
            />
            <span className="text-sm">{label}</span>
          </label>
        ))}
      </div>
      {err && <div className="text-xs text-destructive mt-2" data-testid="notifications-error">{err}</div>}
    </section>
  );
}

function AffidavitSignatureSection() {
  const { user, refresh } = useAuth();
  const [url, setUrl] = useState(user?.signature_url || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { setUrl(user?.signature_url || ""); }, [user?.id, user?.signature_url]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setErr(""); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/user/signature", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newUrl = r.data?.url;
      if (!newUrl) throw new Error("Upload succeeded but no URL returned.");
      // Persist on the user row.
      await api.patch("/users/me", { signature_url: newUrl });
      await refresh?.();
      setUrl(newUrl);
      toast.success("Signature saved.");
    } catch (ex) {
      const msg = formatErr(ex);
      setErr(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <section className="surface p-6" data-testid="affidavit-signature-section">
      <div className="flex items-center gap-3 mb-3">
        <FileText size={20} weight="bold" />
        <div>
          <div className="label-xs">AFFIDAVIT SIGNATURE</div>
          <h2 className="heading text-xl">Signature image</h2>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Stamped on affidavits and proof-of-service documents you sign. Use a transparent PNG for the cleanest result.
      </p>
      <div className="flex items-center gap-3">
        <label className="btn btn-secondary cursor-pointer">
          {busy ? "UPLOADING…" : (url ? "REPLACE SIGNATURE" : "CHOOSE FILE")}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={onPick}
            className="hidden"
            disabled={busy}
            data-testid="signature-upload-input"
          />
        </label>
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-muted-foreground underline truncate max-w-xs"
            data-testid="signature-upload-link"
          >
            {url.split("/").pop()}
          </a>
        )}
      </div>
      {url && (
        <div className="surface p-3 mt-3 inline-flex items-center gap-2" data-testid="signature-preview">
          <ImageIcon size={14} weight="duotone" />
          <img src={url} alt="signature" className="max-h-16 max-w-xs" />
        </div>
      )}
      {err && <div className="text-xs text-destructive mt-2" data-testid="signature-upload-error">{err}</div>}
    </section>
  );
}

function SecuritySection() {
  return (
    <section className="surface p-6" data-testid="security-section">
      <div className="flex items-center gap-3 mb-3">
        <Shield size={20} weight="bold" />
        <div>
          <div className="label-xs">SECURITY</div>
          <h2 className="heading text-xl">Password</h2>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        To change your password, sign out and use the
        {" "}
        <a
          href="/forgot-password"
          className="underline text-primary"
          data-testid="security-reset-link"
        >
          forgot password
        </a>
        {" "}
        flow on the sign-in screen. We don&apos;t store passwords locally — your auth provider handles the reset.
      </p>
    </section>
  );
}

